/**
 * AdminAdjustmentsPage.jsx
 * ==========================
 * Sprint R4B — Internal Diploks Reversal & Adjustment Safety Layer.
 *
 * Super-admin only.  Internal correction tool — NOT a customer refund
 * form.  Records reversals / adjustments under one of the 9 allowed
 * `adjustment_type` codes (duplicate_credit_reversal,
 * failed_payment_credit_reversal, chargeback_reversal, gateway_reversal,
 * admin_correction, fraud_risk_reversal, legal_compliance_reversal,
 * test_cleanup_adjustment, manual_credit_correction).
 *
 * Every adjustment requires: linked wallet id, signed amount, reason,
 * evidence_notes, and an audit_confirmation checkbox.  The page never
 * mutates the original ledger row — instead a new paired ledger row is
 * appended.
 */
import { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, Plus, X, Scale } from "lucide-react";

const ADJUSTMENT_TYPE_LABELS = {
  duplicate_credit_reversal:        "Duplicate credit reversal",
  failed_payment_credit_reversal:   "Failed-payment credit reversal",
  chargeback_reversal:              "Chargeback reversal",
  gateway_reversal:                 "Gateway reversal",
  admin_correction:                 "Admin correction",
  fraud_risk_reversal:              "Fraud / risk reversal",
  legal_compliance_reversal:        "Legal / compliance reversal",
  test_cleanup_adjustment:          "Test / staging cleanup",
  manual_credit_correction:         "Manual credit correction",
};

export default function AdminAdjustmentsPage() {
  const [rows, setRows] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterWallet, setFilterWallet] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (filterType) params.set("adjustment_type", filterType);
      if (filterWallet) params.set("wallet_id", filterWallet);
      const r = await api.get(`/admin/diploks/reversal-adjustment?${params}`);
      setRows(r.data?.adjustments || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [filterType, filterWallet]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <DashboardLayout title="Internal Adjustments">
      <div data-testid="adjustments-page" className="space-y-6">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-900 shadow-sm">
          <div className="flex items-start gap-2">
            <ShieldAlert size={18} className="mt-0.5 text-rose-600" />
            <div>
              <p className="font-semibold">Internal payment-safety tool — super admin only.</p>
              <p className="mt-1 text-xs">
                This is <strong>not</strong> a customer refund form. Use it
                only for payment-safety corrections: duplicate payment,
                failed payment credited by mistake, chargeback, gateway
                reversal, fraud / risk action, legal / compliance
                requirement, admin mistake, or test / staging cleanup.
                Diploks are generally non-refundable platform credits.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-navy-100 bg-white px-5 py-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Scale size={16} className="text-slate-500" />
            <select
              data-testid="adj-filter-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1 text-xs"
            >
              <option value="">All types</option>
              {Object.entries(ADJUSTMENT_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <input
              data-testid="adj-filter-wallet"
              type="text"
              value={filterWallet}
              onChange={(e) => setFilterWallet(e.target.value)}
              placeholder="Filter by wallet id…"
              className="rounded-md border border-slate-300 px-2 py-1 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="adj-refresh"
              onClick={refresh}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw size={12} />
              Refresh
            </button>
            <button
              data-testid="adj-new"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1 rounded-md bg-navy-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-800"
            >
              <Plus size={12} />
              New adjustment
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-navy-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Created</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Wallet</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-right">Before → After</th>
                  <th className="px-4 py-2 text-left">Reason</th>
                  <th className="px-4 py-2 text-left">Admin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                      No internal adjustments recorded under the current filter.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} data-testid={`adj-row-${r.id}`}>
                      <td className="px-4 py-2 font-mono text-xs text-slate-600">
                        {r.created_at?.slice(0, 19).replace("T", " ")}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {ADJUSTMENT_TYPE_LABELS[r.adjustment_type] || r.adjustment_type}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{r.wallet_id}</td>
                      <td className={`px-4 py-2 text-right font-semibold ${r.amount_changed < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                        {r.amount_changed > 0 ? "+" : ""}{r.amount_changed}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-slate-600">
                        {r.balance_before} → {r.balance_after}
                      </td>
                      <td className="px-4 py-2 text-xs max-w-md">{r.reason}</td>
                      <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{r.admin_user_id}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AdjustmentModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onCreated={() => { setModalOpen(false); refresh(); }}
        />
      </div>
    </DashboardLayout>
  );
}

function AdjustmentModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    adjustment_type: "duplicate_credit_reversal",
    wallet_id: "",
    amount_changed: -0,
    reason: "",
    evidence_notes: "",
    original_transaction_id: "",
    original_ledger_id: "",
    original_credit_event_id: "",
    audit_confirmation: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({
        adjustment_type: "duplicate_credit_reversal",
        wallet_id: "",
        amount_changed: 0,
        reason: "",
        evidence_notes: "",
        original_transaction_id: "",
        original_ledger_id: "",
        original_credit_event_id: "",
        audit_confirmation: false,
      });
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!form.wallet_id.trim()) {
      toast.error("Wallet id is required.");
      return;
    }
    if (form.amount_changed === 0 || Number.isNaN(Number(form.amount_changed))) {
      toast.error("Amount must be a non-zero integer.");
      return;
    }
    if (form.reason.trim().length < 8) {
      toast.error("Reason must be at least 8 characters.");
      return;
    }
    if (!form.audit_confirmation) {
      toast.error("Please tick the audit confirmation checkbox.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        amount_changed: Number(form.amount_changed),
        original_transaction_id: form.original_transaction_id || undefined,
        original_ledger_id: form.original_ledger_id || undefined,
        original_credit_event_id: form.original_credit_event_id || undefined,
        evidence_notes: form.evidence_notes || undefined,
      };
      const r = await api.post(`/admin/diploks/reversal-adjustment/create`, body);
      if (r.data?.duplicate) {
        toast.info("Duplicate detected — existing adjustment returned.");
      } else {
        toast.success("Adjustment recorded with audit trail.");
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="adj-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-navy-900">New internal adjustment</h3>
            <p className="mt-1 text-xs text-slate-600">
              Original ledger entries are preserved. This appends a new
              paired reversal/adjustment ledger row.
            </p>
          </div>
          <button data-testid="adj-modal-close" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Type</span>
            <select
              data-testid="adj-type"
              value={form.adjustment_type}
              onChange={(e) => setForm({ ...form, adjustment_type: e.target.value })}
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            >
              {Object.entries(ADJUSTMENT_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Wallet id</span>
            <input
              data-testid="adj-wallet"
              type="text"
              value={form.wallet_id}
              onChange={(e) => setForm({ ...form, wallet_id: e.target.value })}
              placeholder="pwal-abc… or org id"
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Amount changed</span>
            <input
              data-testid="adj-amount"
              type="number"
              value={form.amount_changed}
              onChange={(e) => setForm({ ...form, amount_changed: e.target.value })}
              placeholder="-200 for reversal"
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
            <span className="mt-1 block text-[11px] text-slate-500">
              Negative = reverse (debit). Positive = correct (credit).
            </span>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Linked transaction id</span>
            <input
              data-testid="adj-txn"
              type="text"
              value={form.original_transaction_id}
              onChange={(e) => setForm({ ...form, original_transaction_id: e.target.value })}
              placeholder="pt-…"
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-medium text-slate-600">Linked credit_event_id (optional)</span>
            <input
              data-testid="adj-credit-event"
              type="text"
              value={form.original_credit_event_id}
              onChange={(e) => setForm({ ...form, original_credit_event_id: e.target.value })}
              placeholder="ce-…"
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-medium text-slate-600">Reason (required, min 8 chars)</span>
            <textarea
              data-testid="adj-reason"
              rows={2}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Razorpay raised chargeback CB-12345; reversing the Diploks credit."
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-medium text-slate-600">Evidence / notes (optional)</span>
            <textarea
              data-testid="adj-evidence"
              rows={2}
              value={form.evidence_notes}
              onChange={(e) => setForm({ ...form, evidence_notes: e.target.value })}
              placeholder="Gateway dispute id, support ticket id, screenshots, etc."
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        <label className="mt-4 flex cursor-pointer items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <input
            data-testid="adj-confirm"
            type="checkbox"
            checked={form.audit_confirmation}
            onChange={(e) => setForm({ ...form, audit_confirmation: e.target.checked })}
            className="mt-0.5"
          />
          <span>
            I confirm this internal adjustment is for a clear payment-safety
            reason (duplicate / failed / chargeback / gateway reversal /
            fraud / legal / admin mistake / test cleanup). I understand the
            original ledger entry will be preserved and a new paired
            reversal entry will be appended.
          </span>
        </label>

        <div className="mt-5 flex justify-end gap-3">
          <button
            data-testid="adj-cancel"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            data-testid="adj-submit"
            disabled={
              busy ||
              !form.wallet_id.trim() ||
              Number(form.amount_changed) === 0 ||
              Number.isNaN(Number(form.amount_changed)) ||
              (form.reason || "").trim().length < 8 ||
              !form.audit_confirmation
            }
            onClick={submit}
            className="rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {busy ? "Recording…" : "Record adjustment"}
          </button>
        </div>
      </div>
    </div>
  );
}
